/**
 * API Utility - Centralized axios wrapper with interceptors
 * Automatically handles ApiResponse unwrapping and error handling
 */
import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@tms-phoenix/tms-types';

export interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
  getToken?: () => string | null;
  /**
   * Called when a request returns 401. Should attempt to refresh the access
   * token and return the new token string, or null if refresh failed (in
   * which case the caller is responsible for redirecting to login).
   * When provided, the client will retry the original request once with the
   * fresh token. Concurrent 401s are queued and resolved together once the
   * single in-flight refresh settles.
   */
  onRefreshToken?: () => Promise<string | null>;
  /**
   * URL substrings that should never trigger a token refresh on 401.
   * Prevents infinite retry loops for auth endpoints (login, logout, refresh).
   * Defaults to ['/auth/refresh'] when onRefreshToken is provided.
   */
  skipRefreshForUrls?: string[];
  /**
   * Called when a request that was already retried once with a refreshed
   * token (originalRequest._retry === true) still comes back as 401. This
   * means the refresh succeeded but the session is still invalid — the
   * caller should force a client-side logout instead of attempting another
   * refresh (which would otherwise never be tried again since _retry blocks it).
   */
  onAuthFailure?: () => void | Promise<void>;
}

/**
 * Create a configured axios instance with automatic response/error handling
 *
 * The response interceptor unwraps ApiResponse<T>.data into response.data,
 * so callers access the payload via `response.data` while still having
 * access to `response.status`, `response.headers`, etc.
 *
 * Usage in stores:
 *   const api = createApiClient({ baseURL: config.api.baseUrl, getToken: () => authStore.accessToken });
 *   const { data, status, headers } = await api.get<UserType>('/api/users');
 *   // data = UserType (unwrapped from ApiResponse), status = 200, headers = {...}
 */
export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const instance = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeout || 30000,
  });

  // ── 401 refresh-token mutex (per-instance, closure-scoped) ─────────────────
  let _isRefreshing = false;
  let _failedQueue: Array<{
    resolve: (token: string | null) => void;
    reject: (err: unknown) => void;
  }> = [];

  function processQueue(error: unknown, token: string | null) {
    _failedQueue.forEach(({ resolve, reject }) => {
      error ? reject(error) : resolve(token);
    });
    _failedQueue = [];
  }

  // Request interceptor: Add auth token
  instance.interceptors.request.use((requestConfig) => {
    const token = config.getToken?.();
    if (token && requestConfig.headers) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }

    return requestConfig;
  });

  // Response interceptor: Unwrap ApiResponse and set response.data to the inner payload
  // Callers still receive the full AxiosResponse, so status/headers remain accessible
  instance.interceptors.response.use(
    (response) => {
      const data = response.data;

      // Check if response matches ApiResponse structure
      if (data && typeof data === 'object' && 'success' in data) {
        const apiResponse = data as ApiResponse<unknown>;

        if (!apiResponse.success) {
          // API returned error in success=false response
          const errorMessage = apiResponse.error?.message || 'API request failed';
          throw new Error(errorMessage);
        }

        // Unwrap: replace ApiResponse wrapper with its inner data
        // Only unwrap if the response actually has a `.data` property —
        // some endpoints (e.g. print-agent) return { success, devices, ... }
        // without a nested `data` wrapper.
        if ('data' in apiResponse) {
          response.data = apiResponse.data;
        }

        // Preserve the full meta object so callers can access cursor keys,
        // pagination, and any other metadata attached by the API.
        const meta = apiResponse.meta as Record<string, unknown> | undefined;
        if (meta) {
          ((response as unknown) as Record<string, unknown>).meta = meta;
        }

        // Preserve pagination metadata so list endpoints can access total counts.
        // Callers can read (response as any).pagination or cast to ApiAxiosResponse.
        if (meta?.pagination) {
          // Nested layout: meta.pagination = { total, page, limit, totalPages }
          ((response as unknown) as Record<string, unknown>).pagination = meta.pagination;
        } else if (meta && typeof meta.total === 'number') {
          // Flat layout: meta = { total, page, limit, totalPages, ... }
          ((response as unknown) as Record<string, unknown>).pagination = {
            total: meta.total,
            page: meta.page,
            limit: meta.limit,
            totalPages: meta.totalPages,
          };
        } else {
          // Top-level layout: some endpoints return pagination as a sibling of
          // `data` and `meta` in the envelope (e.g. disposedItems report).
          // Preserve it so callers can access (response as any).pagination.
          const topPagination = (apiResponse as Record<string, unknown>)['pagination'];
          if (topPagination !== undefined) {
            ((response as unknown) as Record<string, unknown>).pagination = topPagination;
          }
        }

        // Preserve top-level `summary` field (used by report endpoints that
        // return a status breakdown alongside the paginated data array).
        const topSummary = (apiResponse as Record<string, unknown>)['summary'];
        if (topSummary !== undefined) {
          ((response as unknown) as Record<string, unknown>).summary = topSummary;
        }

        // Preserve top-level `grpc_url` field (e.g. GET /downloads/grpc-certs/files
        // returns { success, data: files[], grpc_url } — grpc_url is a sibling of
        // `data`, not nested inside it, so it would otherwise be dropped above).
        const topGrpcUrl = (apiResponse as Record<string, unknown>)['grpc_url'];
        if (topGrpcUrl !== undefined) {
          ((response as unknown) as Record<string, unknown>).grpc_url = topGrpcUrl;
        }
      }

      return response;
    },
    async (error: AxiosError<ApiResponse<unknown>>) => {
      const originalRequest = (error.config ?? {}) as InternalAxiosRequestConfig & { _retry?: boolean };

      // ── 401 token-refresh and retry ────────────────────────────────────────
      // Only attempt when:
      //   • a refresh callback is configured
      //   • the response status is 401
      //   • this request has not already been retried
      //   • the URL is not an auth endpoint (prevents infinite loops)
      //
      // Deliberately does NOT require an Authorization header to have been
      // present on the original request. A token can be missing from the
      // request even though the app believes it's authenticated (e.g. the
      // in-memory store's getToken() source was cleared/desynced, or the
      // token disappeared from storage in another tab) — in that case we
      // still want to attempt a refresh (or, if there's no valid refresh
      // token either, let onRefreshToken's failure path force a logout)
      // rather than silently leaving every request 401ing forever.
      if (
        config.onRefreshToken &&
        error.response?.status === 401 &&
        !originalRequest._retry
      ) {
        const skipUrls = config.skipRefreshForUrls ?? ['/auth/refresh'];
        const requestUrl = originalRequest.url ?? '';
        const isSkipped = skipUrls.some((u) => requestUrl.includes(u));

        if (!isSkipped) {
          if (_isRefreshing) {
            // Another refresh is already in flight — queue this request
            return new Promise<string | null>((resolve, reject) => {
              _failedQueue.push({ resolve, reject });
            }).then((token) => {
              if (token && originalRequest.headers) {
                if (typeof originalRequest.headers.set === 'function') {
                  originalRequest.headers.set('Authorization', `Bearer ${token}`);
                } else {
                  (originalRequest.headers as Record<string, unknown>)['Authorization'] = `Bearer ${token}`;
                }
              }
              originalRequest._retry = true;
              return instance.request(originalRequest);
            });
          }

          originalRequest._retry = true;
          _isRefreshing = true;

          try {
            const newToken = await config.onRefreshToken();

            if (newToken) {
              processQueue(null, newToken);
              if (originalRequest.headers) {
                if (typeof originalRequest.headers.set === 'function') {
                  originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
                } else {
                  (originalRequest.headers as Record<string, unknown>)['Authorization'] = `Bearer ${newToken}`;
                }
              }
              return instance.request(originalRequest);
            }

            // onRefreshToken returned null — reject with the original AxiosError
            // so callers that inspect error.response (status, body) still work.
            error.message = 'Token refresh failed';
            processQueue(error, null);
            return Promise.reject(error);
          } catch (refreshError) {
            processQueue(refreshError, null);
            return Promise.reject(refreshError);
          } finally {
            _isRefreshing = false;
          }
        }
      }

      // ── Second 401 after a refresh-retry ────────────────────────────────────
      // originalRequest._retry is only set once we've already retried this
      // request with a freshly-refreshed token. If it 401s again, the refresh
      // didn't actually restore a valid session — don't attempt a second
      // refresh (that would only ever happen via a brand-new request), force
      // a client-side logout instead.
      if (config.onAuthFailure && error.response?.status === 401 && originalRequest._retry) {
        // Swallow rejections (e.g. a failed dynamic import or router error) so they
        // don't surface as an unhandled promise rejection — the original error below
        // is still propagated to the caller regardless of how onAuthFailure resolves.
        Promise.resolve(config.onAuthFailure()).catch(() => {
          // Intentionally ignored — the original 401 error is still rejected below.
        });
      }

      // Enrich error message with the API error body (for non-retried errors)
      if (error.response?.data) {
        const apiResponse = error.response.data;
        if ('error' in apiResponse && apiResponse.error?.message) {
          error.message = apiResponse.error.message;
        } else if (error.response.statusText) {
          error.message = error.response.statusText;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
}
